import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, EmptyValue, Spinner } from '@/components/ui';
import { KPIHomeWidget } from './KPIHomeWidget';
import { formatDay, type PendingTransfersSummary } from '../../services/vehicleTransferService';

export const PENDING_TRANSFERS_WIDGET_PERMISSION = 'widget:pending_transfers';

export interface PendingTransfersWidgetProps {
  summary: PendingTransfersSummary | null;
  loading: boolean;
  error: Error | null;
  onRetry: () => void;
  /** View All, the tiles and the rows open Equipment > Transfers only for users who can view Equipment. */
  canOpenTransfers: boolean;
}

/**
 * V3-059 Pending Transfers, in the Home tile style: the count awaiting approval in the user's
 * service areas and the count that needs the user's action, then up to five transfers (unit,
 * from -> to, Date of Move, waiting on). No inner scroll (LAY-03): five rows at most.
 */
const PendingTransfersWidget: React.FC<PendingTransfersWidgetProps> = ({ summary, loading, error, onRetry, canOpenTransfers }) => {
  if (loading && !summary) {
    return (
      <div className="tw-flex tw-items-center tw-gap-2 tw-py-2" role="status">
        <Spinner animation="border" size="sm" />
        <span className="tw-text-sm tw-text-content-muted">Loading pending transfers…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="tw-mb-0 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <span>Pending transfers could not be loaded. {error.message}</span>
        <Button size="sm" variant="outline-danger" onClick={onRetry}>
          Retry
        </Button>
      </Alert>
    );
  }

  const pending = summary?.pendingCount ?? 0;
  const mine = summary?.needsMyActionCount ?? 0;
  const rows = summary?.rows ?? [];
  const link = (view: string) => (canOpenTransfers ? { path: '/equipment/transfers', queryParams: { view } } : undefined);

  return (
    <div className="tw-flex tw-flex-col tw-gap-2" data-testid="pending-transfers-widget">
      <div className="tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2">
        <KPIHomeWidget
          id="pending-transfers-awaiting"
          title="Awaiting Approval"
          size="small"
          requiredPermission={PENDING_TRANSFERS_WIDGET_PERMISSION}
          data={{ value: pending, description: pending === 1 ? 'Transfer awaiting approval' : 'Transfers awaiting approval' }}
          redirect={link('open')}
          metadata={{ loading: false, error: null, isEmpty: false }}
          onRefresh={onRetry}
        />
        <KPIHomeWidget
          id="pending-transfers-mine"
          title="Needs My Action"
          size="small"
          requiredPermission={PENDING_TRANSFERS_WIDGET_PERMISSION}
          data={{ value: mine, description: mine === 1 ? 'Transfer waiting on you' : 'Transfers waiting on you' }}
          redirect={link('awaiting-me')}
          metadata={{ loading: false, error: null, isEmpty: false }}
          onRefresh={onRetry}
        />
      </div>
      {rows.length > 0 && (
        <ul className="tw-mb-0 tw-divide-y tw-divide-line tw-rounded-lg tw-border tw-border-line tw-bg-surface tw-pl-0" aria-label="Pending transfers">
          {rows.map((row) => (
            <li key={row.id} className="tw-grid tw-list-none tw-grid-cols-2 tw-gap-x-3 tw-px-3 tw-py-2 tw-text-sm md:tw-grid-cols-4">
              <span className="tw-font-medium">
                {canOpenTransfers ? <Link to={`/equipment/transfers/${row.id}`}>{row.unitNumber}</Link> : row.unitNumber}
                {row.needsMyAction && <span className="tw-ml-2 tw-text-xs tw-text-warning-text">Needs My Action</span>}
              </span>
              <span className="tw-whitespace-nowrap">
                {row.from} → {row.to}
              </span>
              <span className="tw-whitespace-nowrap tw-tabular-nums">{formatDay(row.effectiveDate)}</span>
              <span className="tw-truncate" title={row.waitingOn.join(', ')}>
                {row.waitingOn.length > 0 ? `Waiting on ${row.waitingOn.join(', ')}` : row.status === 'APPROVED' ? 'Waiting on Finance' : <EmptyValue />}
              </span>
            </li>
          ))}
        </ul>
      )}
      {canOpenTransfers && (
        <div className="tw-text-right">
          <Link to="/equipment/transfers" className="tw-text-sm">
            View All
          </Link>
        </div>
      )}
    </div>
  );
};

export default PendingTransfersWidget;
